<script setup lang="ts">
import { useExternalUrl } from '@/composables/useExternalUrl';
import { useFetch } from '@/composables/useFetch';
import { usePlatform } from '@/composables/usePlatform';
import { useEnvStore } from '@/stores/envStore';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';

interface CreditPack {
  id: number;
  name: string;
  description: string;
  credits_amount: number;
  price: string;
  currency: string;
}

const props = defineProps<{
  pack: CreditPack;
  isRedirecting: boolean;
}>();

const emit = defineEmits(['update:is-redirecting']);

const { fetchData } = useFetch();
const envStore = useEnvStore();
const toast = useToast();
const { t } = useI18n();
const { navigateToExternalUrl } = useExternalUrl();
const { isNative } = usePlatform();

const handlePurchase = async () => {
  emit('update:is-redirecting', true);
  try {
    const { data, error } = await fetchData(`${envStore.apiUrl}/credit-packs/purchase`, 'POST', {
      credit_pack_id: props.pack.id,
    });

    if (error || !data?.url) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('credits.purchaseError'),
        life: 3000,
      });
    } else {
      if (isNative.value) {
        await navigateToExternalUrl(data.url, { target: '_blank' });
      } else {
        await navigateToExternalUrl(data.url);
      }
    }
  } finally {
    emit('update:is-redirecting', false);
  }
};
</script>

<template>
  <div class="flex flex-col rounded-lg border p-6 text-center shadow-md">
    <h3 class="mb-2 font-semibold text-xl">{{ pack.name }}</h3>
    <p class="mb-4 text-gray-500">{{ pack.description }}</p>
    <div class="my-4 flex flex-row items-end justify-center gap-2">
      <span class="font-bold text-4xl">{{ pack.credits_amount }}</span>
      <span class="text-lg text-gray-600"> {{ t('credits.credits') }}</span>
    </div>
    <div class="mb-6 text-2xl font-light">{{ pack.price }} {{ pack.currency.toUpperCase() }}</div>
    <Button :label="t('credits.buyNow')" icon="pi pi-shopping-cart" :loading="isRedirecting" class="mt-auto" @click="handlePurchase" />
  </div>
</template>
